import { uploadFilesOnVapour } from "Utils/uploadFileOnS3";
import { classNames } from "Utils/utils";
import Breadcrumb from "components/common/Breadcrumb/Breadcrumb";
import CustomButton from "components/common/Button/CustomButton";
import Header from "components/common/Header/Header";
import SideBarNew from "components/common/SideBar/SideBarNew";
import { LoginContext } from "helpers/LoginContext";
import moment from "moment";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useFieldArray } from "react-hook-form";
import {
  AiOutlineInfoCircle,
  AiOutlineSave,
  AiOutlineWarning,
} from "react-icons/ai";
import { BiArrowBack } from "react-icons/bi";
import { FiAlertTriangle, FiCheckCircle, FiChevronRight } from "react-icons/fi";
import { HiOutlineClipboardCheck } from "react-icons/hi";
import { MdOutlineMedication } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import careWatchApi from "store/services/careWatch.services";
import { toast } from "react-toastify";
import { AttachmentsStep } from "./components/steps/AttachmentsStep";
import { BasicDetailsStep } from "./components/steps/BasicDetailsStep";
import { IncidentDetailsStep } from "./components/steps/IncidentDetailsStep";
import { IncidentOverviewStep } from "./components/steps/IncidentOverviewStep";
import { InjuryFormStep } from "./components/steps/InjuryFormStep";
import { InstructionsStep } from "./components/steps/InstructionsStep";
import { MedicationFormStep } from "./components/steps/MedicationFormStep";
import { StepProgressIndicator } from "./components/wizard/StepProgressIndicator";
import {
  ANALYSER_CATEGORY_TO_FORM,
  ANALYSER_MEDICATION_TO_FORM,
  INCIDENT_CATEGORIES,
  INCIDENT_STATUS,
  INJURY_CAUSES,
  MEDICATION_INCIDENT_CATEGORIES,
  OUTCOME_OPTIONS,
  PERSON_INVOLVED_OPTIONS,
} from "./constants";
import { getParticipantLabel, USER_ROLE } from "Constants/constant";
import { SHOW_INCIDENT_TIMESHEET_PICKER } from "pages/CareWatch/utils/constants";
import { roleAccess } from "helpers/RolesPermission";
import { CREATE_INCIDENT_BREADCRUMB } from "./constants/formConfig";
import { OUTCOME_MEDICATION } from "./constants/incidentTypes";
import { backRegions, frontRegions } from "./data/Coordinates";
import { useIncidentForm } from "./hooks/useIncidentForm";
import { useWizardNavigation } from "./hooks/useWizardNavigation";
import { useParticipantSearch } from "./hooks/useParticipantSearch";
import { buildIncidentPayload } from "./utils/payloadBuilder";
import { validateStep } from "./utils/validators";
import { formatTimesheetLabel } from "./utils/timesheetLabel";
import incidentApi from "store/services/incident.services";
import { IMAGE_URL } from "store/services/URL";

const BREADCRUMB = CREATE_INCIDENT_BREADCRUMB;

export const CreateIncidentContainer = (props) => {
  const {
    createIncidentCall,
    createLoading,
    createSuccess,
    createdIncident,
    updateIncidentCall,
    updateLoading,
    updateSuccess,
    updatedIncident,
    resetCreateCall,
    getAssignedParticipantsCall,
    assignedParticipants,
    assignedParticipantsLoading,
    getWorkerListCall,
    workersList,
    getCatalogServicesSubCategoryRequest: stateCatServicesRequestCall,
    catalogSubCategoryStatesLoading,
    stateOptions,
    settings,
    editMode = false,
    isResume = false,
    isEdit = false,
    incidentDetail = null,
    initialUuid = null,
  } = props;

  const isFinalisedEdit =
    editMode &&
    (incidentDetail?.status === INCIDENT_STATUS.REPORTED ||
      incidentDetail?.status === INCIDENT_STATUS.CLOSED);

  const hideSaveAndExit =
    editMode &&
    (incidentDetail?.status === INCIDENT_STATUS.SUBMITTED ||
      incidentDetail?.status === INCIDENT_STATUS.UNDER_INVESTIGATION ||
      isFinalisedEdit);

  // State
  const [incidentUuid, setIncidentUuid] = useState(initialUuid);
  const [pendingNextStep, setPendingNextStep] = useState(false);
  const [isEditDataLoaded, setIsEditDataLoaded] = useState(false);
  const [pendingSaveAndExit, setPendingSaveAndExit] = useState(false);
  const [timesheetOptions, setTimesheetOptions] = useState([]);
  const [timesheetLoading, setTimesheetLoading] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const pendingFinalSubmitRef = useRef(false);
  const isInitializedRef = useRef(false);
  const lastLoadedIncidentIdRef = useRef(null);

  const navigate = useNavigate();
  const { sideBarPadding, isUserGracePeriodOver } = useContext(LoginContext);
  const signaturePadRef = useRef(null);

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    control,
    clearErrors,
    errors,
  } = useIncidentForm();

  const location = useLocation();

  /**
   * Prefill from a promoted Care Watch signal.
   *
   * The signal is identified by `?care_watch=<timesheet uuid>`, so the prefill
   * survives a refresh or a link someone pasted to a colleague. Router state is
   * used when present purely to skip the round trip; the parameter is the
   * source of truth.
   *
   * The narrative fields were written by the timesheet analysis from the shift
   * note — a starting draft the reporter edits and signs, never a submission.
   * Only empty fields are filled, so nothing a reporter typed is replaced.
   */
  const careWatchUuid = new URLSearchParams(location.search).get("care_watch");
  // Which incident on that shift. A group shift can carry more than one, and
  // the form fills from the event the manager opened.
  const careWatchIncidentKey =
    new URLSearchParams(location.search).get("incident_key") || "";
  const pendingMedCats = useRef([]);

  useEffect(() => {
    /**
     * Only fill a field the reporter has not already touched.
     *
     * Several fields carry a default from useIncidentForm ("no" on the yes/no
     * toggles, today's date), so "empty" has to mean empty string or the
     * untouched default — comparing against "" alone would skip them forever.
     */
    const fill = (field, value) => {
      const text = (value ?? "").toString().trim();
      if (!text) return;
      if ((getValues(field) ?? "").toString().trim()) return;
      setValue(field, text);
    };

    /**
     * Yes/No toggles store lowercase "yes"/"no" (see YesNoToggle) and the
     * validators compare against those exact strings. Writing "Yes" leaves the
     * toggle visually unselected AND fails validation, so the case matters.
     *
     * Tri-state on purpose: null means the shift note did not say, and a
     * notifiable record must not claim "No" because nobody mentioned it.
     */
    const fillTriBool = (field, value) => {
      if (typeof value !== "boolean") return;
      setValue(field, value ? "yes" : "no");
    };

    const fillList = (field, values, replace = false) => {
      if (!Array.isArray(values) || values.length === 0) return;
      const existing = getValues(field);
      if (!replace && Array.isArray(existing) && existing.length > 0) return;
      setValue(field, values, { shouldDirty: true, shouldTouch: true });
    };

    const unwrapSignal = (payload) => {
      if (!payload || typeof payload !== "object") return null;
      if (
        payload.incidentDraft ||
        payload.incident_draft ||
        payload.timesheetUuid ||
        payload.timesheetId
      ) {
        return payload;
      }
      return payload.data ? unwrapSignal(payload.data) : payload;
    };

    const applySignal = (raw, { replaceClassification = false } = {}) => {
      const signal = unwrapSignal(raw);
      if (!signal) return;

      // People — the prefill a reporter most expects, and the reason the API
      // returns ids rather than the display names the report stores.
      //
      // Narrowed to the people this incident involves. A group shift can carry
      // two unrelated events, and prefilling everyone rostered put a
      // participant who had nothing happen to them onto a notifiable record
      // unless the reporter noticed and removed them.
      const allParticipants = Array.isArray(signal.participantRefs)
        ? signal.participantRefs
        : Array.isArray(signal.participant_refs)
          ? signal.participant_refs
          : [];
      const involvedIds = (
        signal.incidentParticipantIds ||
        signal.incident_participant_ids ||
        []
      ).map(Number);
      const involved = involvedIds.length
        ? allParticipants.filter((p) => involvedIds.includes(Number(p.id)))
        : [];
      const participants = involved.length > 0 ? involved : allParticipants;
      if (participants.length > 0) {
        fillList(
          "selectedParticipants",
          participants.map((p) => ({
            id: Number(p.id) || 0,
            value: String(p.uuid ?? p.id),
            uuid: String(p.uuid ?? p.id),
            label: `${p.firstName ?? p.first_name ?? ""} ${
              p.lastName ?? p.last_name ?? ""
            }`.trim(),
            first_name: p.firstName ?? p.first_name ?? "",
            last_name: p.lastName ?? p.last_name ?? "",
            profile_picture: "",
          }))
        );
      }

      const worker = signal.workerRef || signal.worker_ref;
      if (worker?.id) {
        fillList("selectedWorkers", [
          {
            id: Number(worker.id) || 0,
            value: String(worker.uuid ?? worker.id),
            uuid: String(worker.uuid ?? worker.id),
            label: `${worker.firstName ?? worker.first_name ?? ""} ${
              worker.lastName ?? worker.last_name ?? ""
            }`.trim(),
            first_name: worker.firstName ?? worker.first_name ?? "",
            last_name: worker.lastName ?? worker.last_name ?? "",
          },
        ]);
      }

      if (careWatchUuid) {
        const workerName =
          `${worker?.firstName ?? worker?.first_name ?? ""} ${
            worker?.lastName ?? worker?.last_name ?? ""
          }`.trim() ||
          (typeof signal.worker === "string" ? signal.worker : "");
        setSelectedTimesheet({
          value: careWatchUuid,
          uuid: careWatchUuid,
          label: formatTimesheetLabel({
            date: signal.shiftDate || signal.shift_date,
            start: signal.workerStartTime || signal.worker_start_time,
            end: signal.workerEndTime || signal.worker_end_time,
            workerName,
          }),
        });
      }

      const addr = signal.incidentAddress || signal.incident_address;
      if (addr && typeof addr === "object") {
        fill("incident_address_street", addr.street);
        fill("incident_address_suburb", addr.suburb);
        fill("incident_address_state", addr.state);
        fill("incident_address_postcode", addr.postcode);
        fill("incident_address", addr.formatted);
      } else if (typeof addr === "string" && addr.trim()) {
        fill("incident_address_street", addr);
        fill("incident_address", addr);
      }

      // When it happened. The form defaults these to today, which is wrong for
      // a shift being written up later, so they are set rather than filled.
      const shiftDate = signal.shiftDate || signal.shift_date;
      if (shiftDate) {
        const day = moment(shiftDate);
        if (day.isValid()) {
          const formatted = day.format("YYYY-MM-DD");
          setValue("incident_overview_date", formatted);
          setIncidentOverviewDate(formatted);
          setIncidentDate(formatted);
        }
      }
      const startTime = signal.workerStartTime || signal.worker_start_time;
      if (startTime) {
        const at = moment(startTime);
        if (at.isValid()) {
          const formatted = at.format("HH:mm");
          setValue("incident_overview_time", formatted);
          setIncidentOverviewTime(formatted);
        }
      }

      /**
       * Narrative and classification, written by the timesheet analysis from
       * the shift note. A starting draft the reporter edits and signs, never a
       * submission. Absent unless the analysis found a real incident.
       */
      const draft = signal.incidentDraft || signal.incident_draft;
      if (!draft) return;

      fill("incident_details_narrative", draft.incident_details_narrative);
      fill("triggers_circumstances", draft.triggers_circumstances);
      fill("immediate_action_taken", draft.immediate_action_taken);
      fill("executive_summary", draft.executive_summary);
      fill("injury_details", draft.injury_details);
      fill("incident_location_details", draft.incident_location_details);

      fillTriBool("injuries_occurred", draft.injuries_occurred);
      fillTriBool(
        "regulated_restrictive_practices_used",
        draft.regulated_restrictive_practices_used
      );

      const rawMedCats = Array.isArray(draft.medication_incident_categories)
        ? draft.medication_incident_categories
        : [];
      const mappedMedCats = rawMedCats
        .map((c) => ANALYSER_MEDICATION_TO_FORM[c] || c)
        .filter((c) =>
          MEDICATION_INCIDENT_CATEGORIES.some((opt) => opt.value === c)
        );
      if (mappedMedCats.length) {
        pendingMedCats.current = mappedMedCats;
        fillList(
          "medication_incident_categories",
          mappedMedCats,
          replaceClassification
        );
      }

      /**
       * Outcome and category, translated server-side into the form's own
       * values (see IncidentDraftFormMapper) so web and mobile prefill the
       * same thing from one analysis.
       *
       * A cached list row may still carry analyser vocab only. Map it here
       * too so Promote does not open with a blank required category.
       */
      const formCategories = Array.isArray(draft.form_categories)
        ? draft.form_categories
        : [];
      const analyserCategories = Array.isArray(draft.incident_category)
        ? draft.incident_category
        : [];
      const mappedFromAnalyser = analyserCategories
        .map((c) => ANALYSER_CATEGORY_TO_FORM[c])
        .filter(Boolean);
      const othersText = String(draft.incident_category_others || "").trim();
      let categoriesToSelect = formCategories.length
        ? formCategories
        : mappedFromAnalyser;

      const outcomeValue =
        draft.outcome_type ||
        (mappedMedCats.length ? "medication" : "incident");
      const unhurt =
        outcomeValue === "hazard" || outcomeValue === "near_miss";
      if (unhurt) {
        categoriesToSelect = categoriesToSelect.filter(
          (category) =>
            category !== "non_serious_injury_pwd" &&
            category !== "serious_injury_pwd"
        );
      }
      const withOthers =
        othersText && !categoriesToSelect.includes("others")
          ? [...categoriesToSelect, "others"]
          : categoriesToSelect;
      if (outcomeValue) {
        setSelectedOutcome((current) =>
          replaceClassification
            ? OUTCOME_OPTIONS.find((o) => o.value === outcomeValue) || current
            : current ||
              OUTCOME_OPTIONS.find((o) => o.value === outcomeValue) ||
              null
        );
      }

      const nextCategories = INCIDENT_CATEGORIES.filter((c) =>
        withOthers.includes(c.value)
      );
      if (nextCategories.length || unhurt || replaceClassification) {
        setIncidentCategory((current) =>
          replaceClassification || unhurt || !current.length
            ? nextCategories
            : current
        );
      }
      setIncidentCategoryOthers((current) =>
        replaceClassification ? othersText : current || othersText
      );
      fill(
        "medication_other_description",
        draft.medication_other_description
      );
      fill("medication_possible_reasons", draft.medication_possible_reasons);
      fill("medication_immediate_action", draft.medication_immediate_action);

      if (unhurt) {
        fillTriBool("injuries_occurred", false);
        setSelectedInjuryCauses([]);
        setInjuryCauseOther("");
        setValue("injury_cause", []);
        setValue("injury_cause_other", "");
      }

      const rawInjuryCauses = unhurt
        ? []
        : Array.isArray(draft.injury_cause)
          ? draft.injury_cause
          : [];
      const nextInjuryCauses = INJURY_CAUSES.filter((cause) =>
        rawInjuryCauses.some(
          (value) =>
            value === cause.value || (value === "others" && cause.value === "other")
        )
      );
      if (nextInjuryCauses.length) {
        setSelectedInjuryCauses((current) =>
          current.length ? current : nextInjuryCauses
        );
        setValue(
          "injury_cause",
          nextInjuryCauses.map((cause) => cause.value)
        );
      }
      if (draft.injury_cause_other) {
        setInjuryCauseOther((current) => current || draft.injury_cause_other);
        fill("injury_cause_other", draft.injury_cause_other);
      }
    };

    // Fast path fills people immediately from the dock. Still fetch the
    // signal so mapper-enriched categories/outcome overwrite a stale list row.
    const fromState = location.state?.prefill?.careWatchSignal;
    if (fromState) {
      applySignal(fromState);
    }

    if (!careWatchUuid) return undefined;

    let cancelled = false;
    careWatchApi
      .getCareWatchSignal(careWatchUuid, careWatchIncidentKey)
      .then((res) => {
        if (!cancelled) {
          applySignal(res?.data, { replaceClassification: true });
        }
      })
      .catch(() => {
        // A missing signal is not an error worth interrupting the reporter for:
        // the form simply opens blank, which is a valid way to file one.
      });

    return () => {
      cancelled = true;
    };
  }, [careWatchUuid, careWatchIncidentKey, location.state, setValue, getValues]);

  const [participantOptions, setParticipantOptions] = useState([]);

  // Participant search hook for async dropdown
  const {
    options: participantSearchOptions,
    isLoading: participantSearchLoading,
    hasMore: hasMoreParticipants,
    handleInputChange: onParticipantInputChange,
    handleMenuScrollToBottom: onParticipantMenuScrollToBottom,
    resetToDefault: onParticipantMenuOpenReset,
  } = useParticipantSearch();
  const [workerOptions, setWorkerOptions] = useState([]);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [witnesses, setWitnesses] = useState([]);
  const [witnessName, setWitnessName] = useState("");
  const [witnessContact, setWitnessContact] = useState("");

  const {
    fields: witnessFields,
    append: appendWitness,
    remove: removeWitnessField,
  } = useFieldArray({
    control,
    name: "incident_details_witnesses",
  });

  const [signatureData, setSignatureData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedBodyParts, setSelectedBodyParts] = useState([]);
  const [personInvolved, setPersonInvolved] = useState([]);
  const [personInvolvedVisitor, setPersonInvolvedVisitor] = useState("");
  const [personInvolvedVolunteer, setPersonInvolvedVolunteer] = useState("");
  const [personInvolvedOther, setPersonInvolvedOther] = useState("");
  const [incidentCategory, setIncidentCategory] = useState([]);
  const [incidentCategoryOthers, setIncidentCategoryOthers] = useState("");
  const [ndisReferenceNumber, setNdisReferenceNumber] = useState("");
  const [selectedInjuryCauses, setSelectedInjuryCauses] = useState([]);
  const [injuryCauseOther, setInjuryCauseOther] = useState("");
  const [incidentDate, setIncidentDate] = useState(
    moment().format("YYYY-MM-DD"),
  );
  const [incidentOverviewDate, setIncidentOverviewDate] = useState(
    moment().format("YYYY-MM-DD"),
  );
  const [incidentOverviewTime, setIncidentOverviewTime] = useState(
    moment().format("HH:mm"),
  );

  // Medication checkboxes live on a later wizard step. Re-apply after the
  // outcome is set so mounting that step cannot leave them empty.
  useEffect(() => {
    if (selectedOutcome?.value !== "medication") return;
    const cats = pendingMedCats.current;
    if (!Array.isArray(cats) || cats.length === 0) return;
    setValue("medication_incident_categories", cats, { shouldDirty: true });
  }, [selectedOutcome, setValue]);

  const injuriesOccurred = watch("injuries_occurred");
  const watchFormData = watch();

  const incidentAddressStreetValue = watch("incident_address_street");
  const incidentAddressSuburbValue = watch("incident_address_suburb");
  const incidentAddressStateValue = watch("incident_address_state");
  const incidentAddressPostcodeValue = watch("incident_address_postcode");

  // Combine address parts into full address
  useEffect(() => {
    const addressParts = [
      incidentAddressStreetValue,
      incidentAddressSuburbValue,
      incidentAddressStateValue,
      incidentAddressPostcodeValue,
    ].filter(Boolean);
    const fullAddress = addressParts.join(", ");
    if (fullAddress) {
      setValue("incident_address", fullAddress);
    }
  }, [
    incidentAddressStreetValue,
    incidentAddressSuburbValue,
    incidentAddressStateValue,
    incidentAddressPostcodeValue,
    setValue,
  ]);

  // Step configuration based on outcome type
  const getStepConfig = useMemo(() => {
    const baseSteps = [
      {
        id: "instructions",
        label: "Instructions",
        icon: HiOutlineClipboardCheck,
      },
      {
        id: "basic_details",
        label: "Basic Details",
        icon: AiOutlineInfoCircle,
      },
    ];

    if (!selectedOutcome) return baseSteps;

    if (selectedOutcome.value === OUTCOME_MEDICATION) {
      return [
        ...baseSteps,
        {
          id: "medication_form",
          label: "Medication Details",
          icon: MdOutlineMedication,
        },
        { id: "attachments", label: "Attachments & Sign", icon: FiCheckCircle },
      ];
    }

    const incidentSteps = [
      ...baseSteps,
      {
        id: "incident_overview",
        label: "Incident Overview",
        icon: FiAlertTriangle,
      },
      {
        id: "incident_details",
        label: "Incident Details",
        icon: AiOutlineInfoCircle,
      },
    ];

    if (injuriesOccurred === "yes") {
      incidentSteps.push({
        id: "injury_form",
        label: "Injury Details",
        icon: AiOutlineWarning,
      });
    }

    incidentSteps.push({
      id: "attachments",
      label: "Attachments & Sign",
      icon: FiCheckCircle,
    });
    return incidentSteps;
  }, [selectedOutcome, injuriesOccurred]);

  // Initialize on mount
  useEffect(() => {
    if (!editMode) resetCreateCall();
    lastLoadedIncidentIdRef.current = null;
    setIsEditDataLoaded(false);

    // Disabled: Now using useParticipantSearch hook for async participant loading
    // getAssignedParticipantsCall();
    getWorkerListCall();
    stateCatServicesRequestCall();

    const timer = setTimeout(() => {
      isInitializedRef.current = true;
    }, 100);

    return () => {
      clearTimeout(timer);
      isInitializedRef.current = false;
      lastLoadedIncidentIdRef.current = null;
      resetCreateCall();
    };
  }, []);

  // Map participant options
  useEffect(() => {
    if (assignedParticipants?.data) {
      const options = assignedParticipants.data.map((p) => ({
        id: p.id,
        value: p.uuid || p.id,
        label: `${p.first_name || ""} ${p.last_name || ""}`.trim(),
        uuid: p.uuid || p.id,
      }));
      setParticipantOptions(options);
    } else {
      setParticipantOptions([]);
    }
  }, [assignedParticipants]);

  // Map worker options
  useEffect(() => {
    if (workersList?.length > 0) {
      const options = workersList.map((w) => ({
        id: w.id || w.value,
        value: w.uuid || String(w.id || w.value),
        label: w.label || `${w.first_name || ""} ${w.last_name || ""}`.trim(),
        uuid: w.uuid,
      }));
      setWorkerOptions(options);
    }
  }, [workersList]);

  const timesheetLocked =
    Boolean(careWatchUuid) || Boolean(editMode && incidentDetail?.timesheet?.uuid);
  const showTimesheetPicker = SHOW_INCIDENT_TIMESHEET_PICKER;
  const requireTimesheet = showTimesheetPicker && !timesheetLocked && !editMode;
  const isWorkerPortal = !roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]);
  const selectedWorkersWatch = watch("selectedWorkers") || [];
  const workerKey = selectedWorkersWatch
    .map((worker) => worker?.id)
    .filter((id) => Number(id) > 0)
    .join(",");
  const previousWorkerKey = useRef(null);

  useEffect(() => {
    if (!showTimesheetPicker || timesheetLocked || isWorkerPortal) return;
    if (previousWorkerKey.current && previousWorkerKey.current !== workerKey) {
      setSelectedTimesheet(null);
      setValue("selectedParticipants", []);
    }
    previousWorkerKey.current = workerKey;
  }, [workerKey, showTimesheetPicker, timesheetLocked, isWorkerPortal, setValue]);

  useEffect(() => {
    if (!showTimesheetPicker || timesheetLocked) return undefined;
    const ids = workerKey ? workerKey.split(",").map((id) => Number(id)) : [];
    if (!isWorkerPortal && ids.length === 0) {
      setTimesheetOptions([]);
      return undefined;
    }
    let cancelled = false;
    setTimesheetLoading(true);
    incidentApi
      .listTimesheets(ids)
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data || {};
        const rows = payload.timesheets || [];
        setTimesheetOptions(
          rows.map((row) => ({
            value: row.uuid,
            uuid: row.uuid,
            label: row.label || row.uuid,
            worker_id: row.worker_id,
          }))
        );
        const scoped = (payload.worker_ids || []).map((id) => Number(id)).filter((id) => id > 0);
        const current = getValues("selectedWorkers") || [];
        if (isWorkerPortal && current.length === 0 && scoped.length > 0 && workerOptions.length > 0) {
          const matches = workerOptions.filter((worker) => scoped.includes(Number(worker.id)));
          if (matches.length > 0) setValue("selectedWorkers", matches);
        }
      })
      .catch(() => {
        if (!cancelled) setTimesheetOptions([]);
      })
      .finally(() => {
        if (!cancelled) setTimesheetLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workerKey, showTimesheetPicker, timesheetLocked, isWorkerPortal, workerOptions, getValues, setValue]);

  useEffect(() => {
    if (editMode && incidentDetail?.timesheet?.uuid) {
      const sheet = incidentDetail.timesheet;
      setSelectedTimesheet({
        value: sheet.uuid,
        uuid: sheet.uuid,
        label: sheet.label || sheet.uuid,
      });
    }
  }, [editMode, incidentDetail]);

  useEffect(() => {
    if (
      editMode &&
      incidentDetail?.workers?.length > 0 &&
      workerOptions?.length > 0
    ) {
      const selectedWorkers = incidentDetail.workers.map((w) => {
        const option = workerOptions.find(
          (opt) =>
            opt?.id === w?.id ||
            opt?.value === w?.uuid ||
            opt?.uuid === w?.uuid,
        );
        return (
          option || {
            id: w?.id,
            value: w?.uuid || String(w?.id),
            label: `${w?.first_name || ""} ${w?.last_name || ""}`.trim(),
            uuid: w?.uuid,
          }
        );
      });
      setValue("selectedWorkers", selectedWorkers);
    }
  }, [editMode, incidentDetail, workerOptions, setValue]);
  
  useEffect(() => {
    if (editMode && incidentDetail) {
      const currentIncidentId = `${incidentDetail?.uuid}-${incidentDetail?.updated_at || "initial"}`;
      if (lastLoadedIncidentIdRef.current === currentIncidentId) return;

      lastLoadedIncidentIdRef.current = currentIncidentId;
      setIsEditDataLoaded(true);

      if (incidentDetail?.participants?.length > 0) {
        const selectedParticipants = incidentDetail.participants.map((p) => {
          const first_name = p?.first_name ? String(p.first_name) : "";
          const last_name = p?.last_name ? String(p.last_name) : "";
          const rawPic = p?.profile_picture ? String(p.profile_picture).trim() : "";
          const profile_picture =
            rawPic === ""
              ? ""
              : rawPic.startsWith("http")
                ? rawPic
                : `${IMAGE_URL}${rawPic}`;
          return {
            id: p?.id ? Number(p.id) : 0,
            value: p?.uuid ? String(p.uuid) : String(p?.id),
            label: `${first_name} ${last_name}`.trim(),
            uuid: p?.uuid ? String(p.uuid) : String(p?.id),
            first_name,
            last_name,
            profile_picture,
          };
        });
        setValue("selectedParticipants", selectedParticipants);
      }

      if (incidentDetail?.workers?.length > 0 && workerOptions.length > 0) {
        const selectedWorkers = incidentDetail.workers.map((w) => {
          const option = workerOptions.find((opt) => opt?.id === w?.id);
          return (
            option || {
              id: w?.id,
              value: w?.id,
              label: `${w?.first_name || ""} ${w?.last_name || ""}`.trim(),
            }
          );
        });
        setValue("selectedWorkers", selectedWorkers);
      }

      // Outcome type
      if (incidentDetail.outcome_type) {
        const outcomeOption = OUTCOME_OPTIONS.find(
          (o) => o.value === incidentDetail.outcome_type,
        );
        setSelectedOutcome(outcomeOption || null);
      }

      // Dates and times (use incident_overview_* or fallback to incident_*)
      if (incidentDetail.incident_date)
        setIncidentDate(incidentDetail.incident_date);
      const overviewDate =
        incidentDetail.incident_overview_date || incidentDetail.incident_date;
      if (overviewDate) {
        setIncidentOverviewDate(overviewDate);
        setValue("incident_overview_date", overviewDate);
      }
      const overviewTime =
        incidentDetail.incident_overview_time || incidentDetail.incident_time;
      if (overviewTime) {
        setIncidentOverviewTime(overviewTime);
        setValue("incident_overview_time", overviewTime);
      }

      // Incident category
      if (incidentDetail.incident_category?.length > 0) {
        const categories = incidentDetail.incident_category.map((val) => {
          const found = INCIDENT_CATEGORIES?.find((c) => c.value === val);
          return found || { value: val, label: val };
        });
        setIncidentCategory(categories);
      }
      if (incidentDetail.incident_category_others)
        setIncidentCategoryOthers(incidentDetail.incident_category_others);
      if (incidentDetail.ndis_reference_number)
        setNdisReferenceNumber(incidentDetail.ndis_reference_number);

      // Person involved
      if (incidentDetail.person_involved?.length > 0) {
        const involved = incidentDetail.person_involved.map((val) => {
          const found = PERSON_INVOLVED_OPTIONS?.find((p) => p.value === val);
          return found || { value: val, label: val };
        });
        setPersonInvolved(involved);
      }
      if (incidentDetail.person_involved_visitor)
        setPersonInvolvedVisitor(incidentDetail.person_involved_visitor);
      if (incidentDetail.person_involved_volunteer)
        setPersonInvolvedVolunteer(incidentDetail.person_involved_volunteer);
      if (incidentDetail.person_involved_other)
        setPersonInvolvedOther(incidentDetail.person_involved_other);

      // Injury fields
      if (incidentDetail.body_parts?.length > 0)
        setSelectedBodyParts(incidentDetail.body_parts);
      if (incidentDetail.injury_cause?.length > 0) {
        const causes = incidentDetail.injury_cause.map((val) => ({
          value: val,
          label: val,
        }));
        setSelectedInjuryCauses(causes);
      }
      if (incidentDetail.injury_cause_other)
        setInjuryCauseOther(incidentDetail.injury_cause_other);

      // Signature
      if (incidentDetail.signature) setSignatureData(incidentDetail.signature);

      // Attachments
      if (incidentDetail.attachments?.length > 0) {
        const existingAttachments = incidentDetail.attachments.map(
          (att, index) => {
            if (typeof att === "string") {
              const fileName = `Attachment ${index + 1}`;
              const fileObj = new File([""], fileName, {
                type: "application/octet-stream",
              });
              return {
                id: index,
                file: fileObj,
                original_name: fileName,
                file_name: fileName,
                name: fileName,
                path: att,
                file_path: att,
                attachment_path: att,
                isExisting: true,
              };
            }
            const fileName =
              att.original_name ||
              att.file_name ||
              att.name ||
              `Attachment ${index + 1}`;
            const filePath =
              att.url ||
              att.file_url ||
              att.path ||
              att.file_path ||
              att.attachment_path ||
              att;
            const fileObj = new File([""], fileName, {
              type: "application/octet-stream",
            });
            return {
              id: att.id || index,
              file: fileObj,
              original_name: fileName,
              file_name: fileName,
              name: fileName,
              path: filePath,
              file_path: filePath,
              attachment_path: filePath,
              isExisting: true,
            };
          },
        );
        setAttachments(existingAttachments);
      }

      // Address
      if (incidentDetail.incident_address) {
        setValue("incident_address_street", incidentDetail.incident_address);
      }

      // Form fields
      const formFields = [
        "incident_date",
        "incident_time",
        "incident_address",
        "incident_location_details",
        "person_responsible_notified",
        "injuries_occurred",
        "first_aid_given",
        "ambulance_needed",
        "prn_administered",
        "reported_to_police",
        "medical_review_offered",
        "executive_summary",
        "has_behavior_management_plan",
        "regulated_restrictive_practices_used",
        "current_task",
        "incident_details_narrative",
        "conclusion",
        "triggers_circumstances",
        "immediate_action_taken",
        "abuse_check",
        "abuse_details",
        "injury_details",
        "treatment_administered",
        "treatment_administrator",
        "medication_incident_categories",
        "medication_other_description",
        "medication_possible_reasons",
        "medication_immediate_action",
        "coordinator_notified",
        "doctor_notified",
        "pharmacist_notified",
        "signatory_name",
      ];

      const booleanFields = [
        "injuries_occurred",
        "person_responsible_notified",
        "first_aid_given",
        "ambulance_needed",
        "reported_to_police",
        "medical_review_offered",
        "prn_administered",
        "has_behavior_management_plan",
        "regulated_restrictive_practices_used",
        "coordinator_notified",
        "doctor_notified",
        "pharmacist_notified",
      ];

      formFields.forEach((field) => {
        if (
          incidentDetail[field] !== undefined &&
          incidentDetail[field] !== null
        ) {
          if (booleanFields.includes(field)) {
            setValue(field, incidentDetail[field] ? "yes" : "no");
          } else {
            setValue(field, incidentDetail[field]);
          }
        }
      });

      // Witnesses
      if (incidentDetail.incident_details_witnesses?.length > 0) {
        setValue(
          "incident_details_witnesses",
          incidentDetail.incident_details_witnesses,
        );
      }
    }
  }, [editMode, incidentDetail, workerOptions, isEditDataLoaded]);

  const previousOutcomeRef = useRef(null);

  // Clear form fields when outcome type changes
  useEffect(() => {
    if (!selectedOutcome) return;

    const outcomeValue = selectedOutcome.value;
    const previousOutcome = previousOutcomeRef.current;
    previousOutcomeRef.current = outcomeValue;

    // Skip during initial edit data load
    if (editMode && !isEditDataLoaded) return;

    // Skip on initial load or same outcome
    if (!previousOutcome || previousOutcome === outcomeValue) return;

    // Clear medication fields when switching away
    if (outcomeValue !== "medication" && previousOutcome === "medication") {
      setValue("medication_incident_categories", []);
      setValue("medication_other_description", "");
      setValue("medication_possible_reasons", "");
      setValue("medication_immediate_action", "");
      setValue("coordinator_notified", "no");
      setValue("doctor_notified", "no");
      setValue("pharmacist_notified", "no");
    }

    // Clear injury fields when switching to medication
    if (outcomeValue === "medication") {
      setValue("injuries_occurred", "no");
      setValue("body_parts", []);
      setValue("injury_cause", []);
      setValue("injury_cause_other", "");
      setValue("injury_details", "");
      setValue("first_aid_given", "no");
      setValue("ambulance_needed", "no");
      setValue("prn_administered", "no");
      setValue("reported_to_police", "no");
      setValue("medical_review_offered", "no");
      setValue("abuse_check", "");
      setValue("abuse_details", "");
      setValue("treatment_administered", "");
      setValue("treatment_administrator", "");
      setSelectedBodyParts([]);
      setSelectedInjuryCauses([]);
      setInjuryCauseOther("");
    }
  }, [selectedOutcome, editMode, isEditDataLoaded]);

  // Handle create success
  useEffect(() => {
    if (!isInitializedRef.current) return;

    if (createSuccess && createdIncident && !editMode) {
      if (createdIncident?.status === "submitted") {
        navigate(`/incident-management/view/${createdIncident?.uuid}`);
      } else {
        if (pendingSaveAndExit) {
          setPendingSaveAndExit(false);
          setIsUploading(false);
          navigate("/incident-management");
          return;
        }

        if (pendingNextStep) {
          setPendingNextStep(false);
          navigate(`/incident-management/edit/${createdIncident?.uuid}`, {
            replace: true,
          });
        }
      }
    }
  }, [createSuccess, createdIncident, editMode]);

  // Handle update success
  useEffect(() => {
    if (!isInitializedRef.current) return;

    if (updateSuccess && updatedIncident) {
      if (pendingSaveAndExit) {
        setPendingSaveAndExit(false);
        setIsUploading(false);
        navigate("/incident-management");
        return;
      }

      if (pendingNextStep) {
        setPendingNextStep(false);
        forceNext();
      }

      const savedFinalisedIncident =
        isFinalisedEdit &&
        ["reported", "closed"].includes(updatedIncident?.status);
      if (
        pendingFinalSubmitRef.current &&
        (updatedIncident?.status === "submitted" || savedFinalisedIncident)
      ) {
        pendingFinalSubmitRef.current = false;
        navigate(`/incident-management/view/${updatedIncident?.uuid}`);
        return;
      }

      // Safety reset: avoid a stale flag causing later redirects.
      pendingFinalSubmitRef.current = false;
    }
  }, [updateSuccess, updatedIncident, isFinalisedEdit]);

  // Reset pending state on error
  useEffect(() => {
    if (
      (pendingSaveAndExit || pendingFinalSubmitRef.current) &&
      !createLoading &&
      !updateLoading &&
      !createSuccess &&
      !updateSuccess
    ) {
      setPendingSaveAndExit(false);
      pendingFinalSubmitRef.current = false;
      setIsUploading(false);
    }
  }, [
    createLoading,
    updateLoading,
    createSuccess,
    updateSuccess,
    pendingSaveAndExit,
  ]);

  const canProceed = (stepId) => {
    return validateStep(stepId, watch(), {
      selectedParticipants: watch("selectedParticipants") || [],
      selectedWorkers: watch("selectedWorkers") || [],
      selectedTimesheet,
      timesheetLocked,
      requireTimesheet,
      selectedOutcome,
      personInvolvedVisitor,
      personInvolvedVolunteer,
      personInvolvedOther,
      incidentCategory,
      selectedBodyParts,
      signatureData,
      getWatchValue: watch,
    });
  };

  const getInitialStep = () => {
    if (editMode && incidentDetail) {
      if (isResume && incidentDetail.current_step) {
        return Math.max(0, incidentDetail.current_step - 1);
      }
      if (isEdit) return 0;
    }
    return 0;
  };

  const {
    currentStep,
    currentStepId,
    isFinalStep,
    validationAttempted,
    handleNext,
    forceNext,
    handleBack,
    handleStepClick,
    navigateToStep,
  } = useWizardNavigation(
    getStepConfig,
    canProceed,
    getInitialStep(),
    Boolean(editMode),
  );

  // Scroll to top when navigating between report pages so content is not shown from center
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const hasNavigatedToResumeStepRef = useRef(false);
  const hasNavigatedToSectionRef = useRef(false);
  const requestedStepId = location.state?.stepId;

  // Navigate to saved step on resume
  useEffect(() => {
    if (hasNavigatedToResumeStepRef.current || requestedStepId) return;

    if (
      editMode &&
      isResume &&
      isEditDataLoaded &&
      selectedOutcome &&
      incidentDetail?.current_step &&
      getStepConfig.length > 2
    ) {
      hasNavigatedToResumeStepRef.current = true;

      const stepIdMap = {
        1: "instructions",
        2: "basic_details",
        3:
          selectedOutcome?.value === "medication"
            ? "medication_form"
            : "incident_overview",
        4: "incident_details",
        5: "injury_form",
        6: "attachments",
      };

      const targetStepId = stepIdMap[incidentDetail.current_step];
      if (targetStepId) {
        const foundIndex = getStepConfig.findIndex(
          (s) => s.id === targetStepId,
        );
        if (foundIndex > 0 && foundIndex < getStepConfig.length) {
          navigateToStep(foundIndex);
        }
      }
    }
  }, [
    editMode,
    isResume,
    isEditDataLoaded,
    selectedOutcome,
    incidentDetail?.current_step,
    getStepConfig,
    navigateToStep,
    requestedStepId,
  ]);

  // Open the section chosen from the incident view.
  useEffect(() => {
    if (hasNavigatedToSectionRef.current) return;
    if (!editMode || !requestedStepId || !isEditDataLoaded) return;

    const foundIndex = getStepConfig.findIndex((step) => step.id === requestedStepId);
    if (foundIndex < 0) return;

    hasNavigatedToSectionRef.current = true;
    navigateToStep(foundIndex);
  }, [editMode, requestedStepId, isEditDataLoaded, getStepConfig, navigateToStep]);

  const getStepNumber = (stepId) => {
    const stepMap = {
      instructions: 1,
      basic_details: 2,
      incident_overview: 3,
      medication_form: 3,
      incident_details: 4,
      injury_form: 5,
      attachments: 6,
    };
    return stepMap[stepId] || 1;
  };

  // Extract numeric ID from selection object
  const getNumericId = (selected, options) => {
    if (!selected) return null;
    if (selected.id && typeof selected.id === "number") return selected.id;
    if (
      selected.id &&
      typeof selected.id === "string" &&
      !isNaN(parseInt(selected.id))
    ) {
      return parseInt(selected.id);
    }
    if (selected.value && typeof selected.value === "number")
      return selected.value;
    if (
      selected.value &&
      typeof selected.value === "string" &&
      !isNaN(parseInt(selected.value))
    ) {
      return parseInt(selected.value);
    }
    if (options?.length > 0) {
      const found = options.find(
        (opt) =>
          opt.value === selected.value ||
          opt.uuid === selected.value ||
          opt.id === selected.id ||
          opt.value === selected.uuid,
      );
      if (found?.id) return found.id;
    }
    return null;
  };
  const getDefaultSaveStatus = () => {
    if (isFinalisedEdit) {
      return incidentDetail.status;
    }
    if (editMode && incidentDetail?.status && incidentDetail.status !== "draft") {
      return incidentDetail.status;
    }
    return "draft";
  };

  const buildStepPayload = (stepNumber, status = getDefaultSaveStatus()) => {
    const data = watch();
    const selectedParticipantsList = watch("selectedParticipants") || [];
    const selectedWorkersList = watch("selectedWorkers") || [];

    return {
      current_step: stepNumber,
      status,
      participant_ids: selectedParticipantsList
        .map((p) => getNumericId(p, participantOptions))
        .filter(Boolean),
      worker_ids: selectedWorkersList
        .map((w) => getNumericId(w, workerOptions))
        .filter(Boolean),
      ...((selectedTimesheet?.uuid || selectedTimesheet?.value || careWatchUuid)
        ? {
            timesheet_uuid:
              selectedTimesheet?.uuid || selectedTimesheet?.value || careWatchUuid,
          }
        : {}),
      outcome_type: selectedOutcome?.value,
      incident_date: data.incident_date || incidentDate,
      incident_time: data.incident_time,
      incident_overview_date:
        data.incident_overview_date || incidentOverviewDate,
      incident_overview_time:
        data.incident_overview_time || incidentOverviewTime,
      incident_address: data.incident_address || "",
      incident_location_details: data.incident_location_details || "",
      person_responsible_notified: data.person_responsible_notified === "yes",
      incident_category: incidentCategory.map((c) => c.value),
      incident_category_others: incidentCategoryOthers || null,
      ndis_reference_number: ndisReferenceNumber || null,
      person_involved: personInvolved.map((p) => p.value),
      person_involved_visitor: personInvolvedVisitor || null,
      person_involved_volunteer: personInvolvedVolunteer || null,
      person_involved_other: personInvolvedOther || null,
      injuries_occurred: data.injuries_occurred === "yes",
      first_aid_given: data.first_aid_given === "yes",
      ambulance_needed: data.ambulance_needed === "yes",
      prn_administered: data.prn_administered === "yes",
      reported_to_police: data.reported_to_police === "yes",
      medical_review_offered: data.medical_review_offered === "yes",
      executive_summary: data.executive_summary || null,
      has_behavior_management_plan: data.has_behavior_management_plan === "yes",
      regulated_restrictive_practices_used:
        data.regulated_restrictive_practices_used === "yes",
      current_task: data.current_task || null,
      incident_details_narrative: data.incident_details_narrative || null,
      conclusion: data.conclusion || null,
      triggers_circumstances: data.triggers_circumstances || null,
      immediate_action_taken: data.immediate_action_taken || null,
      incident_details_witnesses: data.incident_details_witnesses || [],
      body_parts: selectedBodyParts,
      injury_cause: selectedInjuryCauses.map((c) => c.value),
      injury_cause_other: injuryCauseOther || null,
      injury_details: watch("injury_details") || null,
      abuse_check: watch("abuse_check") || null,
      abuse_details:
        watch("abuse_check") === "yes"
          ? watch("abuse_details") || null
          : null,
      treatment_administered: watch("treatment_administered") || null,
      treatment_administrator:
        watch("treatment_administered") === "yes"
          ? watch("treatment_administrator") || null
          : null,
      medication_incident_categories: data.medication_incident_categories,
      medication_other_description: data.medication_other_description,
      medication_possible_reasons: data.medication_possible_reasons,
      medication_immediate_action: data.medication_immediate_action,
      coordinator_notified: data.coordinator_notified,
      doctor_notified: data.doctor_notified,
      pharmacist_notified: data.pharmacist_notified,
    };
  };

  const saveAndContinue = async () => {
    if (currentStepId === "instructions") {
      handleNext();
      return;
    }
    if (!canProceed(currentStepId)) return;

    // Save next step for refresh persistence
    const nextStepIndex = currentStep + 1;
    const nextStepId = getStepConfig[nextStepIndex]?.id || currentStepId;
    const payload = buildStepPayload(getStepNumber(nextStepId));

    setPendingNextStep(true);
    if (incidentUuid) {
      updateIncidentCall({ uuid: incidentUuid, ...payload });
    } else {
      createIncidentCall(payload);
    }
  };

  const handleSaveAndExit = async () => {
    if (currentStepId === "instructions" && !incidentUuid) {
      navigate("/incident-management");
      return;
    }

    if (!canProceed("basic_details")) {
      toast.warning(
        requireTimesheet
          ? "Select a worker, a timesheet, at least one participant, and an outcome type to save."
          : `Please select at least one ${getParticipantLabel(settings?.care_sector, false).toLowerCase()} and outcome type to save.`
      );
      return;
    }

    try {
      setIsUploading(true);
      setPendingSaveAndExit(true);
      const payload = buildStepPayload(getStepNumber(currentStepId));

      if (currentStepId === "attachments") {
        const newFiles = attachments.filter(
          (att) => !att.isExisting && att instanceof File,
        );
        const existingFiles = attachments.filter((att) => att.isExisting);

        let uploadedNewFiles = [];
        if (newFiles.length > 0) {
          uploadedNewFiles = await uploadFilesOnVapour(newFiles);
        }

        payload.attachments = [
          ...existingFiles.map((att) => ({
            name: att.original_name || att.file_name || att.name,
            original_name: att.original_name || att.file_name || att.name,
            file_name: att.original_name || att.file_name || att.name,
            path: att.path || att.file_path || att.attachment_path,
            file_path: att.path || att.file_path || att.attachment_path,
            url: att.path || att.file_path || att.attachment_path,
          })),
          ...uploadedNewFiles,
        ];
        if (signatureData) payload.signature = signatureData;
        payload.signatory_name = watch("signatory_name") || null;
      }

      if (incidentUuid) {
        updateIncidentCall({ uuid: incidentUuid, ...payload });
      } else {
        createIncidentCall(payload);
      }
    } catch (error) {
      toast.error("Failed to save draft");
      setPendingSaveAndExit(false);
      setIsUploading(false);
    }
  };

  const addWitness = () => {
    if (witnessName.trim()) {
      setWitnesses([
        ...witnesses,
        { name: witnessName, contact: witnessContact },
      ]);
      setWitnessName("");
      setWitnessContact("");
    }
  };

  const removeWitness = (index) => {
    setWitnesses(witnesses.filter((_, i) => i !== index));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (attachments.length + files.length > 5) {
      toast.error("Maximum 5 files allowed");
      return;
    }
    setAttachments([...attachments, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const clearSignature = () => {
    signaturePadRef.current?.clear();
    setSignatureData(null);
  };

  const saveSignature = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      setSignatureData(signaturePadRef.current.toDataURL());
    }
  };

  const handleBodyPartChange = (parts) => {
    setSelectedBodyParts(parts);
    const allRegions = [...frontRegions, ...backRegions];
    const names = parts
      .map((id) => allRegions.find((r) => r.id === id)?.name)
      .filter(Boolean)
      .join(", ");
    setValue("body_parts", names);
  };

  const onSubmit = async (data) => {
    if (isUserGracePeriodOver) return;
    // Only allow final submission when on the attachments step (prevents accidental
    // submit e.g. when user presses Enter in Other(s) or other text fields on earlier steps)
    if (currentStepId !== "attachments") return;

    try {
      setIsUploading(true);
      pendingFinalSubmitRef.current = true;

      const newFiles = attachments.filter(
        (att) => !att.isExisting && att instanceof File,
      );
      const existingFiles = attachments.filter((att) => att.isExisting);

      let uploadedNewFiles = [];
      if (newFiles.length > 0) {
        uploadedNewFiles = await uploadFilesOnVapour(newFiles);
      }

      const allFiles = [
        ...existingFiles.map((att) => ({
          name: att.original_name || att.file_name || att.name,
          original_name: att.original_name || att.file_name || att.name,
          file_name: att.original_name || att.file_name || att.name,
          path: att.path || att.file_path || att.attachment_path,
          file_path: att.path || att.file_path || att.attachment_path,
          url: att.path || att.file_path || att.attachment_path,
        })),
        ...uploadedNewFiles,
      ];

      const selectedParticipantsList = watch("selectedParticipants") || [];
      const selectedWorkersList = watch("selectedWorkers") || [];
      const participantIdsResolved = selectedParticipantsList
        .map((p) => getNumericId(p, participantOptions))
        .filter(Boolean);
      const workerIdsResolved = selectedWorkersList
        .map((w) => getNumericId(w, workerOptions))
        .filter(Boolean);

      const payload = buildIncidentPayload({
        status: isFinalisedEdit ? incidentDetail?.status : "submitted",
        formData: data,
        selectedParticipants: participantIdsResolved.map((id) => ({ id })),
        selectedWorker: workerIdsResolved.map((id) => ({ id })),
        selectedOutcome,
        selectedInjuryCauses,
        injuryCauseOther,
        selectedBodyParts,
        incidentCategory,
        incidentCategoryOthers,
        ndisReferenceNumber,
        personInvolved,
        personInvolvedVisitor,
        personInvolvedVolunteer,
        personInvolvedOther,
        incidentDate,
        incidentOverviewDate,
        incidentOverviewTime,
        witnesses,
        uploadedFiles: allFiles,
        signatureData,
        getWatchValue: watch,
        timesheetUuid:
          selectedTimesheet?.uuid || selectedTimesheet?.value || careWatchUuid,
      });

      payload.current_step = 6;

      if (incidentUuid) {
        updateIncidentCall({
          uuid: incidentUuid,
          ...payload,
          meta: { isFinalSubmit: true },
        });
      } else {
        createIncidentCall(payload);
      }
    } catch (error) {
      pendingFinalSubmitRef.current = false;
      toast.error("Failed to upload files");
    } finally {
      setIsUploading(false);
    }
  };

  // Step renderers
  const renderInstructionsStep = () => <InstructionsStep />;

  const onTimesheetChange = (option) => {
    const next = option?.uuid || option?.value ? option : null;
    setSelectedTimesheet(next);
    if (timesheetLocked) return;
    if (!next?.uuid && !next?.value) {
      setValue("selectedParticipants", []);
      return;
    }
    const uuid = next.uuid || next.value;
    incidentApi
      .timesheetParticipants(uuid)
      .then((res) => {
        const rows = res?.data?.participants || res?.participants || [];
        setValue(
          "selectedParticipants",
          rows.map((person) => ({
            id: Number(person.id) || 0,
            value: String(person.uuid ?? person.id),
            uuid: String(person.uuid ?? person.id),
            label: `${person.first_name || ""} ${person.last_name || ""}`.trim(),
            first_name: person.first_name || "",
            last_name: person.last_name || "",
            profile_picture: "",
          })),
          { shouldDirty: true, shouldTouch: true }
        );
      })
      .catch(() => {
        toast.error("Could not load participants for that timesheet.");
      });
  };

  const renderBasicDetailsStep = () => (
    <BasicDetailsStep
      participantOptions={participantOptions}
      assignedParticipantsLoading={assignedParticipantsLoading}
      workerOptions={workerOptions}
      showWorkerField={true}
      showTimesheetPicker={showTimesheetPicker}
      timesheetOptions={
        timesheetLocked && selectedTimesheet
          ? [selectedTimesheet]
          : timesheetOptions
      }
      timesheetLoading={timesheetLoading}
      selectedTimesheet={selectedTimesheet}
      onTimesheetChange={onTimesheetChange}
      timesheetLocked={timesheetLocked}
      control={control}
      setValue={setValue}
      watch={watch}
      clearErrors={clearErrors}
      selectedOutcome={selectedOutcome}
      setSelectedOutcome={setSelectedOutcome}
      participantLabel={getParticipantLabel(settings?.care_sector, false)}
      participantLabelPlural={getParticipantLabel(settings?.care_sector, true)}
      participantSearchOptions={participantSearchOptions}
      participantSearchLoading={participantSearchLoading}
      hasMoreParticipants={hasMoreParticipants}
      onParticipantInputChange={onParticipantInputChange}
      onParticipantMenuScrollToBottom={onParticipantMenuScrollToBottom}
      onParticipantMenuOpen={onParticipantMenuOpenReset}
    />
  );

  const renderMedicationFormStep = () => (
    <MedicationFormStep register={register} watch={watch} setValue={setValue} />
  );

  const   renderIncidentOverviewStep = () => (
    <IncidentOverviewStep
      incidentCategory={incidentCategory}
      setIncidentCategory={setIncidentCategory}
      incidentCategoryOthers={incidentCategoryOthers}
      setIncidentCategoryOthers={setIncidentCategoryOthers}
      personInvolvedVisitor={personInvolvedVisitor}
      setPersonInvolvedVisitor={setPersonInvolvedVisitor}
      personInvolvedVolunteer={personInvolvedVolunteer}
      setPersonInvolvedVolunteer={setPersonInvolvedVolunteer}
      personInvolvedOther={personInvolvedOther}
      setPersonInvolvedOther={setPersonInvolvedOther}
      incidentOverviewDate={incidentOverviewDate}
      setIncidentOverviewDate={setIncidentOverviewDate}
      incidentOverviewTime={incidentOverviewTime}
      setIncidentOverviewTime={setIncidentOverviewTime}
      validationAttempted={validationAttempted}
      register={register}
      watch={watch}
      setValue={setValue}
      errors={errors}
    />
  );

  const renderIncidentDetailsStep = () => (
    <IncidentDetailsStep
      selectedOutcome={selectedOutcome}
      register={register}
      watch={watch}
      setValue={setValue}
      witnessFields={witnessFields}
      appendWitness={appendWitness}
      removeWitnessField={removeWitnessField}
    />
  );

  const renderInjuryFormStep = () => (
    <InjuryFormStep
      selectedInjuryCauses={selectedInjuryCauses}
      setSelectedInjuryCauses={setSelectedInjuryCauses}
      injuryCauseOther={injuryCauseOther}
      setInjuryCauseOther={setInjuryCauseOther}
      selectedBodyParts={selectedBodyParts}
      handleBodyPartChange={handleBodyPartChange}
      register={register}
      watch={watch}
      setValue={setValue}
      errors={errors}
    />
  );

  const renderAttachmentsStep = () => (
    <AttachmentsStep
      attachments={attachments}
      handleFileChange={handleFileChange}
      removeAttachment={removeAttachment}
      signaturePadRef={signaturePadRef}
      saveSignature={saveSignature}
      clearSignature={clearSignature}
      signatureData={signatureData}
      register={register}
      watch={watch}
      setValue={setValue}
      setAttachments={setAttachments}
    />
  );

  const renderCurrentStep = () => {
    switch (currentStepId) {
      case "instructions":
        return renderInstructionsStep();
      case "basic_details":
        return renderBasicDetailsStep();
      case "medication_form":
        return renderMedicationFormStep();
      case "incident_overview":
        return renderIncidentOverviewStep();
      case "incident_details":
        return renderIncidentDetailsStep();
      case "injury_form":
        return renderInjuryFormStep();
      case "attachments":
        return renderAttachmentsStep();
      default:
        return null;
    }
  };

  const getNextButtonLabel = () => {
    if (currentStepId === "instructions") return "Continue to Report";
    if (isFinalStep && currentStepId !== "basic_details") {
      return isFinalisedEdit ? "Save" : "Submit";
    }
    return "Continue";
  };

  return (
    <div className="min-h-full">
      <SideBarNew />
      <div
        className={classNames(
          "flex flex-col",
          sideBarPadding
            ? "lg:pl-[4rem] duration-300"
            : "lg:pl-64 duration-300",
        )}
      >
        <main className="flex-1 bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen">
          <Header
            title={
              isResume
                ? `Resume Incident Report${incidentDetail?.id ? ` - IN-${incidentDetail.id}` : ""}`
                : isEdit
                  ? `Edit Incident Report${incidentDetail?.id ? ` - IN-${incidentDetail.id}` : ""}`
                  : "New Incident Report"
            }
            breadcrumb={
              <Breadcrumb
                pages={
                  editMode
                    ? BREADCRUMB.map((page, index) =>
                      index === BREADCRUMB.length - 1
                        ? {
                          ...page,
                          name: isResume
                            ? "Resume Incident Report"
                            : "Edit Incident Report",
                        }
                        : page,
                    )
                    : BREADCRUMB
                }
              />
            }
          />

          <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Progress Header */}
              <div className="px-8 pt-8 pb-6 border-b border-gray-100">
                <StepProgressIndicator
                  steps={getStepConfig}
                  currentStep={currentStep}
                  onStepClick={handleStepClick}
                  allowAllSteps={Boolean(editMode)}
                />
              </div>

              {/* Step Content */}
              <div className="px-8 py-8">
                <FormProvider
                  {...{
                    register,
                    handleSubmit,
                    watch,
                    setValue,
                    control,
                    errors,
                  }}
                >
                  <form
                    onSubmit={(e) => {
                      if (currentStepId !== "attachments") {
                        e.preventDefault();
                        return;
                      }
                      handleSubmit(onSubmit)(e);
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        currentStepId !== "attachments" &&
                        ["INPUT", "TEXTAREA"].includes(e.target?.tagName)
                      ) {
                        e.preventDefault();
                      }
                    }}
                  >
                    {renderCurrentStep()}

                    {/* Navigation Buttons */}
                    <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <CustomButton
                          type="button"
                          variant="secondary"
                          label={currentStep === 0 ? "Cancel" : "Back"}
                          Icon={<BiArrowBack className="w-5 h-5" />}
                          clickHandler={
                            currentStep === 0
                              ? () => navigate("/incident-management")
                              : handleBack
                          }
                        />

                        {/* Save & Exit Button */}
                        {currentStep > 0 && !hideSaveAndExit && (
                          <CustomButton
                            type="button"
                            variant="wsecondary"
                            label="Save & Exit"
                            Icon={
                              pendingSaveAndExit ? null : (
                                <AiOutlineSave className="w-5 h-5" />
                              )
                            }
                            clickHandler={handleSaveAndExit}
                            isDisabled={
                              createLoading ||
                              updateLoading ||
                              isUploading ||
                              pendingSaveAndExit ||
                              (currentStepId === "basic_details" && assignedParticipantsLoading)
                            }
                            showLoading={pendingSaveAndExit}
                          />
                        )}
                      </div>

                      {isFinalStep && currentStepId !== "basic_details" ? (
                        <CustomButton
                          type="submit"
                          variant="btn-green"
                          label={getNextButtonLabel()}
                          Icon={
                            createLoading ||
                              updateLoading ||
                              isUploading ? null : (
                              <FiCheckCircle className="w-5 h-5" />
                            )
                          }
                          isDisabled={
                            !canProceed(currentStepId) ||
                            createLoading ||
                            updateLoading ||
                            isUploading ||
                            isUserGracePeriodOver
                          }
                          showLoading={
                            createLoading || updateLoading || isUploading
                          }
                        />
                      ) : (
                        <CustomButton
                          type="button"
                          variant="primary"
                          label={getNextButtonLabel()}
                          Icon={<FiChevronRight className="w-5 h-5" />}
                          clickHandler={saveAndContinue}
                          isDisabled={
                            !canProceed(currentStepId) ||
                            createLoading ||
                            updateLoading ||
                            (currentStepId === "basic_details" && assignedParticipantsLoading)
                          }
                          showLoading={createLoading || updateLoading}
                        />
                      )}
                    </div>
                  </form>
                </FormProvider>
              </div>
            </div>
          </div>
        </main>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </div>
  );
};
